import React, {createContext, useContext, useEffect, useState} from "react";
import ReactDOM from "react-dom";
import {useTranslation} from "react-i18next";
import {isEqual} from "lodash";
import Swal from "sweetalert2";

import CloseBtn from "../../utilities/Buttons/CloseBtn";
import {swalCustom} from "../../utilities/hooks/swalCustom";
import {toaster} from "../../utilities/hooks/toaster";

import ModalWithTabsNew from "./ModalWithTabsNew";

const ModalContext = createContext();

export function useModal() {
  return useContext(ModalContext);
}

function ModalProvider({children}) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [modalProps, setModalProps] = useState(null);
  const [couldClose, setCouldClose] = useState(true);
  const [initialFormData, setInitialFormData] = useState(null);

  const {t} = useTranslation();

  const openModal = (content, props) => {
    setModalContent(content); // Устанавливаем контент для модального окна

    setCouldClose(props?.closeBtnActive === undefined ? true : props?.closeBtnActive);
    setInitialFormData(props?.initialFormData || null);
    setModalProps({...props, setModalOpen});
    setModalOpen(true);
  };

  const closeModal = formData => {
    if (!formData) {
      setModalOpen(false);
      setModalContent(null);
      return;
    }
    if (isModalOpen) {
      if (couldClose) {
        if (formData) {
          if (!isEqual(initialFormData, formData)) throw new Error("could not be closed");
          return;
        }
        setModalOpen(false);
        setModalContent(null);
      } else toaster.info(t("You have unsaved issues"));
    }
  };

  return (
    <ModalContext.Provider
      value={{
        isModalOpen,
        openModal,
        closeModal,
        couldClose,
        setCouldClose,
        initialFormData,
        setInitialFormData,
      }}
    >
      {children}
      {isModalOpen && !modalProps?.withTabs && <Modal {...modalProps}>{modalContent}</Modal>}
      {isModalOpen && modalProps?.withTabs && (
        <ModalWithTabsNew {...modalProps} tabs={modalProps.tabs} modalContent={modalContent} />
      )}
    </ModalContext.Provider>
  );
}

// Компонент модального окна, рендерящий разные компоненты через `children`
export function Modal({
  children,
  label,
  closeBtnActive = true,
  closableOnEsc = true,
  className = "",
  modalInnerWrapperClassName = "",
  style,
  onModalClose = () => {},
  noLabel = false,
  closeBtnStyle = {},
  askBeforeClose = false,
  beforeCloseSwalText,
  ...props
}) {
  const {closeModal, couldClose} = useModal();
  const {t} = useTranslation();

  useEffect(() => {
    if (closableOnEsc) {
      window.addEventListener("keyup", handleEscapeKey);
    }
    return () => {
      window.removeEventListener("keyup", handleEscapeKey);
    };
  }, []);

  const removeFocusFromButton = isActive => {
    if (isActive) {
      const openButton = document.activeElement;
      if (openButton && openButton.blur) {
        openButton.blur(); // remove focus on button when modal was closed by Escape
      }
    }
  };

  useEffect(() => {
    removeFocusFromButton();
  }, []);

  const onClose = async () => {
    if (props?.additionalCloseCondition) {
      await props
        ?.additionalCloseCondition()
        .then(() => {
          onModalClose();
          closeModal();
          return;
        })
        .catch(() => {
          Swal.fire({
            title: t("Close the modal?"),
            text: t("You have unsaved issues"),
            icon: "info",
            confirmButtonText: t("Close"),
            showCancelButton: true,
            cancelButtonText: t("Cancel"),

            customClass: {
              popup: "zindex",
              container: "zindex",
              htmlContainer: "zindex",
            },
          }).then(answ => {
            if (answ.isConfirmed) {
              onModalClose();
              closeModal();
              return;
            } else {
              return;
            }
          });
        });
    } else if (askBeforeClose) {
      swalCustom.confirm({
        text: beforeCloseSwalText,
        confirmFunc: () => {
          onModalClose();
          closeModal();
        },
      });
    } else {
      onModalClose();
      closeModal();
    }
  };

  const handleEscapeKey = event => {
    if (event.key === "Escape" && closableOnEsc) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-form__container ">
      <div className={`${className} modal_form`} style={style}>
        {!noLabel && <label className="label modal_form_label">{typeof label === "string" ? t(label) : label}</label>}

        {closeBtnActive && (
          <CloseBtn
            onClick={() => {
              if (couldClose) onClose();
            }}
            style={closeBtnStyle}
          />
        )}

        <div className="overflow_form_modal">
          <div className={`${modalInnerWrapperClassName} modal_form__child`}>
            {React.cloneElement(children, {...props})}
          </div>
        </div>
      </div>
    </div>,
    document.getElementById("modal-root"),
  );
}

export default ModalProvider;
